import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Ban,
  Search,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Users,
  FileText,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  DollarSign,
  Shield
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Suspension types
const suspensionTypes = [
  { id: 'behavioral', label: 'Behavioral', color: 'text-red-600 bg-red-50 border-red-200' },
  { id: 'technical', label: 'Technical Violation', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { id: 'financial', label: 'Financial', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'safety', label: 'Safety Violation', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { id: 'administrative', label: 'Administrative', color: 'text-slate-600 bg-slate-50 border-slate-200' },
];

// Suspension statuses
const suspensionStatuses = [
  { id: 'active', label: 'Active', color: 'text-red-600 bg-red-50 border-red-200' },
  { id: 'pending', label: 'Pending Review', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'appealed', label: 'Under Appeal', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'served', label: 'Served', color: 'text-slate-600 bg-slate-50 border-slate-200' },
  { id: 'overturned', label: 'Overturned', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
];

// Mock suspensions data
const mockSuspensions = [
  {
    id: 1,
    entity_type: 'driver',
    entity_id: 101,
    name: 'John Smith',
    car_number: '7X',
    team: 'Smith Racing',
    suspension_type: 'behavioral',
    status: 'active',
    reason: 'Unsportsmanlike conduct - intentional contact under caution',
    event_name: 'Knoxville Nationals',
    event_date: '2025-08-15',
    start_date: '2025-08-16',
    end_date: '2025-09-16',
    races_remaining: 3,
    fine_amount: 5000,
    fine_paid: false,
    points_deducted: 50,
    created_by: 'Race Director',
    created_at: '2025-08-15',
    notes: 'Driver made intentional contact with competitor on lap 15 while under yellow flag conditions.',
    appeal_deadline: '2025-08-22',
  },
  {
    id: 2,
    entity_type: 'driver',
    entity_id: 102,
    name: 'Mike Johnson',
    car_number: '22',
    team: 'Johnson Motorsports',
    suspension_type: 'technical',
    status: 'pending',
    reason: 'Engine displacement violation',
    event_name: 'Williams Grove Speedway',
    event_date: '2025-07-28',
    start_date: null,
    end_date: null,
    races_remaining: null,
    fine_amount: 10000,
    fine_paid: false,
    points_deducted: 100,
    created_by: 'Tech Inspector',
    created_at: '2025-07-28',
    notes: 'Post-race inspection found engine displacement exceeds class limit by 15cc.',
    appeal_deadline: '2025-08-04',
  },
  {
    id: 3,
    entity_type: 'team',
    entity_id: 201,
    name: 'Thunder Racing',
    car_number: '99',
    team: null,
    suspension_type: 'financial',
    status: 'active',
    reason: 'Unpaid entry fees - 3 events',
    event_name: null,
    event_date: null,
    start_date: '2025-07-01',
    end_date: null,
    races_remaining: null,
    fine_amount: 1500,
    fine_paid: false,
    points_deducted: 0,
    created_by: 'Series Administrator',
    created_at: '2025-07-01',
    notes: 'Team has outstanding entry fees totaling $1,500 for Lincoln, Eldora, and Knoxville events.',
    appeal_deadline: null,
  },
  {
    id: 4,
    entity_type: 'driver',
    entity_id: 103,
    name: 'Dave Williams',
    car_number: '15W',
    team: 'Williams Family Racing',
    suspension_type: 'safety',
    status: 'served',
    reason: 'Safety equipment violation',
    event_name: 'Eldora Speedway',
    event_date: '2025-06-20',
    start_date: '2025-06-21',
    end_date: '2025-06-28',
    races_remaining: 0,
    fine_amount: 500,
    fine_paid: true,
    points_deducted: 25,
    created_by: 'Safety Official',
    created_at: '2025-06-20',
    notes: 'Driver found with expired fire suit certification. One race suspension served.',
    appeal_deadline: null,
  },
  {
    id: 5,
    entity_type: 'driver',
    entity_id: 104,
    name: 'Chris Anderson',
    car_number: '3A',
    team: 'Anderson Racing',
    suspension_type: 'behavioral',
    status: 'appealed',
    reason: 'Altercation in pit area',
    event_name: 'Volusia Speedway Park',
    event_date: '2025-02-10',
    start_date: '2025-02-11',
    end_date: '2025-03-11',
    races_remaining: 2,
    fine_amount: 2500,
    fine_paid: true,
    points_deducted: 75,
    created_by: 'Race Director',
    created_at: '2025-02-10',
    notes: 'Physical altercation with crew member from another team. Appeal hearing scheduled for Feb 17.',
    appeal_deadline: '2025-02-17',
  },
];

// Status Badge Component
function StatusBadge({ status }) {
  const statusConfig = suspensionStatuses.find(s => s.id === status) || suspensionStatuses[0];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
      {status === 'active' && <Ban className="w-3 h-3" />}
      {status === 'pending' && <Clock className="w-3 h-3" />}
      {status === 'appealed' && <AlertCircle className="w-3 h-3" />}
      {status === 'served' && <CheckCircle2 className="w-3 h-3" />}
      {status === 'overturned' && <XCircle className="w-3 h-3" />}
      {statusConfig.label}
    </span>
  );
}

// Type Badge Component
function TypeBadge({ type }) {
  const typeConfig = suspensionTypes.find(t => t.id === type) || suspensionTypes[0];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${typeConfig.color}`}>
      {typeConfig.label}
    </span>
  );
}

// Suspension Card Component
function SuspensionCard({ suspension, onEdit, onView }) {
  const [expanded, setExpanded] = useState(false);

  const getDaysRemaining = () => {
    if (!suspension.end_date) return null;
    const end = new Date(suspension.end_date);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const daysRemaining = getDaysRemaining();

  return (
    <Card className={`transition-all ${suspension.status === 'active' ? 'border-red-200 bg-red-50/30' : ''}`}>
      <CardContent className="p-0">
        {/* Main Row */}
        <div
          className="flex items-center gap-4 p-4 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {/* Entity Icon */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            suspension.status === 'active' ? 'bg-red-100' : 'bg-slate-100'
          }`}>
            {suspension.entity_type === 'driver' ? (
              <User className={`w-6 h-6 ${suspension.status === 'active' ? 'text-red-600' : 'text-slate-500'}`} />
            ) : (
              <Users className={`w-6 h-6 ${suspension.status === 'active' ? 'text-red-600' : 'text-slate-500'}`} />
            )}
          </div>

          {/* Entity Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">{suspension.name}</h3>
              {suspension.car_number && (
                <span className="text-sm font-mono text-slate-500">#{suspension.car_number}</span>
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">{suspension.reason}</p>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            <TypeBadge type={suspension.suspension_type} />
            <StatusBadge status={suspension.status} />
          </div>

          {/* Days/Races Remaining */}
          {suspension.status === 'active' && (
            <div className="text-right">
              {suspension.races_remaining !== null ? (
                <div>
                  <div className="text-lg font-bold text-red-600">{suspension.races_remaining}</div>
                  <div className="text-xs text-slate-500">races left</div>
                </div>
              ) : daysRemaining !== null ? (
                <div>
                  <div className="text-lg font-bold text-red-600">{daysRemaining}</div>
                  <div className="text-xs text-slate-500">days left</div>
                </div>
              ) : (
                <div className="text-sm text-red-600 font-medium">Indefinite</div>
              )}
            </div>
          )}

          {/* Expand Icon */}
          <button className="p-1 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-slate-200 bg-slate-50 p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {suspension.event_name && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Incident Event</div>
                  <div className="text-sm font-medium text-slate-700">{suspension.event_name}</div>
                  {suspension.event_date && (
                    <div className="text-xs text-slate-500">{new Date(suspension.event_date).toLocaleDateString()}</div>
                  )}
                </div>
              )}

              {suspension.start_date && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Start Date</div>
                  <div className="text-sm font-medium text-slate-700">
                    {new Date(suspension.start_date).toLocaleDateString()}
                  </div>
                </div>
              )}

              {suspension.end_date && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">End Date</div>
                  <div className="text-sm font-medium text-slate-700">
                    {new Date(suspension.end_date).toLocaleDateString()}
                  </div>
                </div>
              )}

              {suspension.fine_amount > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Fine</div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">
                      ${suspension.fine_amount.toLocaleString()}
                    </span>
                    {suspension.fine_paid ? (
                      <Badge variant="success" size="sm">Paid</Badge>
                    ) : (
                      <Badge variant="danger" size="sm">Unpaid</Badge>
                    )}
                  </div>
                </div>
              )}

              {suspension.points_deducted > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Points Deducted</div>
                  <div className="text-sm font-medium text-red-600">-{suspension.points_deducted}</div>
                </div>
              )}

              {suspension.appeal_deadline && suspension.status !== 'served' && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Appeal Deadline</div>
                  <div className="text-sm font-medium text-slate-700">
                    {new Date(suspension.appeal_deadline).toLocaleDateString()}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs text-slate-500 mb-1">Issued By</div>
                <div className="text-sm font-medium text-slate-700">{suspension.created_by}</div>
              </div>
            </div>

            {suspension.notes && (
              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-1">Notes</div>
                <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border border-slate-200">
                  {suspension.notes}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button variant="secondary" size="sm" onClick={() => onView(suspension)}>
                <FileText className="w-4 h-4 mr-1" />
                View Details
              </Button>
              <Button variant="secondary" size="sm" onClick={() => onEdit(suspension)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              {suspension.status === 'active' && !suspension.fine_paid && suspension.fine_amount > 0 && (
                <Button variant="secondary" size="sm">
                  <DollarSign className="w-4 h-4 mr-1" />
                  Record Payment
                </Button>
              )}
              {suspension.status === 'active' && (
                <Button variant="secondary" size="sm">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Mark Served
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// New Suspension Modal
function SuspensionModal({ isOpen, onClose, suspension, onSave }) {
  const [formData, setFormData] = useState(() => {
    if (suspension) {
      return {
        entity_type: suspension.entity_type || 'driver',
        name: suspension.name || '',
        car_number: suspension.car_number || '',
        team: suspension.team || '',
        suspension_type: suspension.suspension_type || 'behavioral',
        reason: suspension.reason || '',
        event_name: suspension.event_name || '',
        event_date: suspension.event_date || '',
        start_date: suspension.start_date || '',
        end_date: suspension.end_date || '',
        races_remaining: suspension.races_remaining || '',
        fine_amount: suspension.fine_amount || '',
        points_deducted: suspension.points_deducted || '',
        notes: suspension.notes || '',
      };
    }
    return {
      entity_type: 'driver',
      name: '',
      car_number: '',
      team: '',
      suspension_type: 'behavioral',
      reason: '',
      event_name: '',
      event_date: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      races_remaining: '',
      fine_amount: '',
      points_deducted: '',
      notes: '',
    };
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {suspension ? 'Edit Suspension' : 'New Suspension'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Entity Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
              <Select
                value={formData.entity_type}
                onChange={(e) => setFormData({ ...formData, entity_type: e.target.value })}
              >
                <option value="driver">Driver</option>
                <option value="team">Team</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Suspension Type</label>
              <Select
                value={formData.suspension_type}
                onChange={(e) => setFormData({ ...formData, suspension_type: e.target.value })}
              >
                {suspensionTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Name and Car Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {formData.entity_type === 'driver' ? 'Driver Name' : 'Team Name'}
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Car Number</label>
              <Input
                value={formData.car_number}
                onChange={(e) => setFormData({ ...formData, car_number: e.target.value })}
                placeholder="e.g., 49"
              />
            </div>
          </div>

          {/* Team (for drivers) */}
          {formData.entity_type === 'driver' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Team</label>
              <Input
                value={formData.team}
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
                placeholder="Team name"
              />
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason for Suspension</label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Brief description of violation"
              required
            />
          </div>

          {/* Incident Event */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Incident Event</label>
              <Input
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                placeholder="Event name (optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Event Date</label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              />
            </div>
          </div>

          {/* Duration */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                placeholder="Leave blank for indefinite"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Races to Serve</label>
              <Input
                type="number"
                value={formData.races_remaining}
                onChange={(e) => setFormData({ ...formData, races_remaining: e.target.value })}
                placeholder="Optional"
                min="0"
              />
            </div>
          </div>

          {/* Penalties */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fine Amount ($)</label>
              <Input
                type="number"
                value={formData.fine_amount}
                onChange={(e) => setFormData({ ...formData, fine_amount: e.target.value })}
                placeholder="0"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Points Deducted</label>
              <Input
                type="number"
                value={formData.points_deducted}
                onChange={(e) => setFormData({ ...formData, points_deducted: e.target.value })}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Additional Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Detailed description, circumstances, witnesses, etc."
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {suspension ? 'Update Suspension' : 'Issue Suspension'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Suspensions() {
  useParams();
  // eslint-disable-next-line no-unused-vars
  const currentSeries = useSeries();

  const [suspensions, setSuspensions] = useState(mockSuspensions);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSuspension, setEditingSuspension] = useState(null);

  // Filter suspensions
  const filteredSuspensions = suspensions.filter(suspension => {
    const matchesSearch = suspension.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      suspension.reason.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (suspension.car_number && suspension.car_number.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || suspension.status === filterStatus;
    const matchesType = filterType === 'all' || suspension.suspension_type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Stats
  const stats = {
    active: suspensions.filter(s => s.status === 'active').length,
    pending: suspensions.filter(s => s.status === 'pending').length,
    appealed: suspensions.filter(s => s.status === 'appealed').length,
    unpaidFines: suspensions.filter(s => s.fine_amount > 0 && !s.fine_paid).reduce((sum, s) => sum + s.fine_amount, 0),
  };

  const handleEdit = (suspension) => {
    setEditingSuspension(suspension);
    setShowModal(true);
  };

  const handleView = (suspension) => {
    // Could open a detail view or navigate to a detail page
    console.log('View suspension:', suspension);
  };

  const handleSave = (formData) => {
    if (editingSuspension) {
      // Update existing
      setSuspensions(prev => prev.map(s =>
        s.id === editingSuspension.id ? { ...s, ...formData } : s
      ));
    } else {
      // Add new
      setSuspensions(prev => [...prev, {
        ...formData,
        id: Date.now(),
        status: 'active',
        fine_paid: false,
        created_by: 'Current User',
        created_at: new Date().toISOString().split('T')[0],
        appeal_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }]);
    }
    setEditingSuspension(null);
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <Ban className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="page-title">Suspensions</h1>
            <p className="page-subtitle">Manage driver and team disciplinary actions</p>
          </div>
        </div>
        <Button onClick={() => { setEditingSuspension(null); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          New Suspension
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.active}</div>
                <div className="text-sm text-slate-500">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.pending}</div>
                <div className="text-sm text-slate-500">Pending Review</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{stats.appealed}</div>
                <div className="text-sm text-slate-500">Under Appeal</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">${stats.unpaidFines.toLocaleString()}</div>
                <div className="text-sm text-slate-500">Unpaid Fines</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, car number, or reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <div className="w-48">
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {suspensionStatuses.map(status => (
                  <option key={status.id} value={status.id}>{status.label}</option>
                ))}
              </Select>
            </div>

            {/* Type Filter */}
            <div className="w-48">
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                {suspensionTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suspensions List */}
      <div className="space-y-4">
        {filteredSuspensions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Suspensions Found</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No active suspensions for this series'}
              </p>
              {!searchQuery && filterStatus === 'all' && filterType === 'all' && (
                <Button variant="secondary" onClick={() => { setEditingSuspension(null); setShowModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Issue First Suspension
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredSuspensions.map(suspension => (
            <SuspensionCard
              key={suspension.id}
              suspension={suspension}
              onEdit={handleEdit}
              onView={handleView}
            />
          ))
        )}
      </div>

      {/* Results count */}
      {filteredSuspensions.length > 0 && (
        <div className="mt-4 text-sm text-slate-500 text-center">
          Showing {filteredSuspensions.length} of {suspensions.length} suspensions
        </div>
      )}

      {/* New/Edit Modal */}
      <SuspensionModal
        key={editingSuspension?.id ?? 'new'}
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingSuspension(null); }}
        suspension={editingSuspension}
        onSave={handleSave}
      />
    </div>
  );
}
